(() => {
  const CART_EVENT_PREFIX = 'missence:cart';

  const dispatchCartEvent = (eventName, detail = {}) => {
    document.dispatchEvent(
      new CustomEvent(`${CART_EVENT_PREFIX}:${eventName}`, {
        detail,
      })
    );
  };

  const getDrawer = () => document.querySelector('cart-drawer');
  const parser = new DOMParser();

  const parseSection = (html, selector) => {
    if (!html) return null;

    const doc = parser.parseFromString(html, 'text/html');
    return selector ? doc.querySelector(selector) : doc.body;
  };

  const replaceInnerHTML = (target, html, selector) => {
    if (!target || !html) return false;

    const source = parseSection(html, selector);
    if (!source) return false;

    target.innerHTML = source.innerHTML;
    return true;
  };

  const syncCartBubble = (cartData) => {
    const bubble = document.getElementById('cart-icon-bubble');
    const bubbleHtml = cartData?.sections?.['cart-icon-bubble'];
    if (!bubble || !bubbleHtml) return false;

    return replaceInnerHTML(bubble, bubbleHtml);
  };

  const syncDrawerMarkup = (cartData) => {
    const drawer = getDrawer();
    const drawerHtml = cartData?.sections?.['cart-drawer'];
    if (!drawer || !drawerHtml) return false;

    const drawerDialog = drawer.querySelector('#CartDrawer');
    const synced = replaceInnerHTML(drawerDialog, drawerHtml, '#CartDrawer');
    if (!synced) return false;

    const sourceDrawer = parseSection(drawerHtml, 'cart-drawer');
    const sourceItems = parseSection(drawerHtml, 'cart-drawer-items');
    const isEmpty =
      typeof cartData?.item_count === 'number'
        ? cartData.item_count === 0
        : Boolean(sourceDrawer?.classList.contains('is-empty') || sourceItems?.classList.contains('is-empty'));
    drawer.classList.toggle('is-empty', isEmpty);
    drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
    drawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);

    return true;
  };

  const syncCartPageMarkup = (cartData) => {
    const cartPage = document.querySelector('.missence-cart-page');
    const sectionWrapper = cartPage?.closest('.shopify-section');
    if (!cartPage || !sectionWrapper?.id) return false;

    const sectionId = sectionWrapper.id.replace(/^shopify-section-/, '');
    const sectionHtml = cartData?.sections?.[sectionId];
    if (!sectionHtml) return false;

    const source = parseSection(sectionHtml, '.shopify-section');
    if (!source) return false;

    sectionWrapper.innerHTML = source.innerHTML;
    return true;
  };

  const refreshCartSection = (sectionId, selector, target) => {
    if (!target) return Promise.resolve(false);

    return fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((html) => replaceInnerHTML(target, html, selector))
      .catch((error) => {
        console.error(error);
        return false;
      });
  };

  const refreshCartPageIfNeeded = (cartData) => {
    const cartPage = document.querySelector('.missence-cart-page');
    const sectionWrapper = cartPage?.closest('.shopify-section');
    if (!cartPage || !sectionWrapper?.id) return Promise.resolve(false);

    if (syncCartPageMarkup(cartData)) return Promise.resolve(true);

    const sectionId = sectionWrapper.id.replace(/^shopify-section-/, '');
    return fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((html) => {
        const source = parseSection(html, '.shopify-section');
        if (!source) return false;

        sectionWrapper.innerHTML = source.innerHTML;
        return true;
      })
      .catch((error) => {
        console.error(error);
        return false;
      });
  };

  const refreshDrawerIfNeeded = (cartData) => {
    const drawer = getDrawer();
    if (!drawer) return Promise.resolve(false);
    if (syncDrawerMarkup(cartData)) return Promise.resolve(true);

    const target = drawer.querySelector('#CartDrawer');
    return refreshCartSection('cart-drawer', '#CartDrawer', target);
  };

  const refreshBubbleIfNeeded = (cartData) => {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return Promise.resolve(false);
    if (syncCartBubble(cartData)) return Promise.resolve(true);

    return refreshCartSection('cart-icon-bubble', '.shopify-section', bubble);
  };

  const syncRealtimeCartUI = (detail = {}) => {
    const cartData = detail.cartData;
    if (!cartData) return;
    const drawerRenderedBySource = ['cart-drawer', 'product-form', 'form', 'missence-cart', 'event'].includes(
      detail.source
    );

    if (!drawerRenderedBySource) {
      refreshBubbleIfNeeded(cartData);
      refreshDrawerIfNeeded(cartData);
    }

    refreshCartPageIfNeeded(cartData);
  };

  const getCartAddPathname = () => {
    if (!window.routes || !window.routes.cart_add_url) return '/cart/add';

    try {
      return new URL(window.routes.cart_add_url, window.location.origin).pathname;
    } catch (error) {
      return '/cart/add';
    }
  };

  const matchesCartAddForm = (form) => {
    if (!form || form.tagName !== 'FORM' || !form.action) return false;

    try {
      return new URL(form.action, window.location.origin).pathname === getCartAddPathname();
    } catch (error) {
      return false;
    }
  };

  const setFormLoadingState = (form, isLoading) => {
    const submitButton = form.querySelector('[type="submit"]');
    if (!submitButton) return;

    submitButton.toggleAttribute('aria-disabled', isLoading);
    submitButton.classList.toggle('loading', isLoading);

    const spinner = form.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  };

  const addItem = (formData, options = {}) => {
    const drawer = getDrawer();
    const config = fetchConfig('javascript');

    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    if (drawer) {
      formData.append(
        'sections',
        drawer.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      if (options.triggeredBy) drawer.setActiveElement(options.triggeredBy);
    }

    config.body = formData;

    dispatchCartEvent('add-start', {
      source: options.source || 'missence-cart',
      formData,
    });

    return fetch(window.routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: options.source || 'missence-cart',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });

          dispatchCartEvent('error', {
            source: options.source || 'missence-cart',
            productVariantId: formData.get('id'),
            response,
          });

          throw response;
        }

        return publish(PUB_SUB_EVENTS.cartUpdate, {
          source: options.source || 'missence-cart',
          productVariantId: formData.get('id'),
          cartData: response,
        }).then(() => {
          if (drawer) {
            drawer.renderContents(response);
          } else {
            window.location.href = window.routes.cart_url;
          }

          dispatchCartEvent('added', {
            source: options.source || 'missence-cart',
            productVariantId: formData.get('id'),
            cartData: response,
          });

          return response;
        });
      });
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!matchesCartAddForm(form) || form.closest('product-form')) return;

    event.preventDefault();

    const formData = new FormData(form);
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

    setFormLoadingState(form, true);

    addItem(formData, {
      source: form.dataset.cartSource || 'form',
      triggeredBy: document.activeElement,
    })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setFormLoadingState(form, false);
      });
  });

  document.addEventListener(`${CART_EVENT_PREFIX}:add`, (event) => {
    const detail = event.detail || {};
    const formData = new FormData();

    if (detail.items && Array.isArray(detail.items)) {
      detail.items.forEach((item, index) => {
        Object.entries(item).forEach(([key, value]) => {
          formData.append(`items[${index}][${key}]`, value);
        });
      });
    } else {
      Object.entries(detail).forEach(([key, value]) => {
        if (['source', 'triggeredBy'].includes(key) || typeof value === 'undefined' || value === null) return;
        formData.append(key, value);
      });
    }

    addItem(formData, {
      source: detail.source || 'event',
      triggeredBy: detail.triggeredBy,
    }).catch((error) => {
      console.error(error);
    });
  });

  document.addEventListener(`${CART_EVENT_PREFIX}:updated`, (event) => {
    syncRealtimeCartUI(event.detail || {});
  });

  document.addEventListener(`${CART_EVENT_PREFIX}:added`, (event) => {
    syncRealtimeCartUI(event.detail || {});
  });

  if (!window.__missenceCartRealtimeSubscriber && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    window.__missenceCartRealtimeSubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      syncRealtimeCartUI(event || {});
    });
  }

  window.MissenceCart = {
    add(detail = {}) {
      dispatchCartEvent('add', detail);
    },
    open(triggeredBy) {
      dispatchCartEvent('open', { triggeredBy });
    },
    close() {
      dispatchCartEvent('close');
    },
    getDrawer,
  };
})();
